import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, Navigate, useLocation } from "react-router-dom";
import type { AmaQuestion, CandidacyDetail } from "@shared/types";
import { candidacyPath, candidatePath, candidateShareUrl } from "@shared/share-links";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { editToken } from "../lib/editToken";
import { useUserToken, setUserToken, refreshUserToken } from "../lib/userToken";
import { Page, Card, RichLoader, ErrorBox } from "../components/ui";
import { useDelayedFlag } from "../lib/useDelayedFlag";
import { useWaitMessage } from "../lib/useWaitMessage";
import { useT } from "../i18n";
import { usePageTitle } from "../lib/usePageTitle";
import { Stat } from "../components/Stat";
import { fmtDmg, dateLabel, sinceLabel, agoLabel } from "../lib/format";
import { ClaimBadge, OriginBadge, ReviewBadge } from "../components/StatusBadge";
import { MeasuresEditor, type DraftMeasure } from "../components/MeasuresEditor";
import { RichText } from "../components/RichText";
import { RichTextEditor } from "../components/RichTextEditor";
import { docHasText, docOrLegacy } from "../lib/richdoc";
import { Share } from "../components/Share";

// Mounted on BOTH the canonical /u/:username/c/:candidacyId route and the
// legacy deep /c/:countryId/race/:office/:candidacyId route. The legacy route
// still loads (old links keep working) but immediately redirects to the clean
// nested URL once the username is known — one shareable URL everywhere.
export default function Candidacy() {
  const t = useT();
  const { username = "", candidacyId = "" } = useParams();
  const [params] = useSearchParams();
  const { search } = useLocation();
  const justCreated = params.get("created") === "1";
  // The id is canonical on both routes now; the username-resolving fetch stays
  // as a fallback for any stale bookmark that hits this component without one.
  // The per-user edit token is the credential (sent on writes), shared reactively
  // with the nav account menu (copy/restore/forget live there now). Migrate any
  // legacy per-candidacy token into the per-user key on mount.
  const token = useUserToken();
  // Refetch when the token changes (paste-to-restore / forget in the account menu):
  // `mine` is computed server-side from the token we send, so without this dep a
  // freshly-restored owner would never see the edit button until a manual reload.
  const { data, loading, error } = useAsync(
    () => (candidacyId ? api.candidacy(candidacyId) : api.candidacyByUsername(username)),
    [username, candidacyId, token],
  );
  const showRich = useDelayedFlag(loading, 400);
  const waitKey = useWaitMessage(loading);

  const [cd, setCd] = useState<CandidacyDetail | null>(null);
  useEffect(() => setCd(data), [data]);
  usePageTitle(
    cd ? t("candidacy.shareTitle", { name: cd.username, office: cd.office === "president" ? t("office.presidentTitle") : t("office.congressTitle") }) : null,
  );

  // Ownership of THIS page is decided server-side: `mine` is true only when our
  // token resolves to the player behind this campaign. Holding any token no longer
  // unlocks every page — edit/answer show only on the candidate's own page.
  const isOwner = !!cd?.mine;
  useEffect(() => {
    if (cd && editToken.get(cd.id)) refreshUserToken();
  }, [cd]);

  // Legacy deep URL → canonical nested URL (replace, search params survive:
  // ?created=1). Only published campaigns: a pending/hidden admin preview must
  // stay on the deep URL.
  if (!username && cd?.username && cd.review === "published") {
    return <Navigate to={`${candidacyPath(cd.username, cd.id)}${search}`} replace />;
  }

  // Verifying ownership means this is now the owner's own page — store the token,
  // flip mine + the ✓verified badge immediately, without waiting for a refetch.
  const onUnlock = (t: string) => {
    setUserToken(t);
    setCd((prev) => (prev ? { ...prev, claimStatus: "claimed", mine: true } : prev));
  };

  return (
    <Page>
      {cd && (
        <div className="flex flex-wrap gap-x-4">
          <Link to={candidatePath(cd.username)} className="text-accent text-sm">← {cd.username}</Link>
          <Link to={`/c/${cd.countryId}/race/${cd.office}`} className="text-accent text-sm">{t("candidacy.allCandidates")}</Link>
        </div>
      )}
      {showRich && <RichLoader headline={t("candidacy.loadingCandidate", { name: username || "…" })} message={t(waitKey)} />}
      {error && <ErrorBox msg={error} />}

      {cd && (
        <>
          {justCreated && !token && <CreatedBanner />}

          {/* flex-wrap: on phones the Share + badge cluster drops to its own
              line instead of crushing the username to "limbo…". */}
          <div className="flex flex-wrap items-center justify-between my-3 gap-2">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold truncate">
                <Link to={candidatePath(cd.username)} className="hover:underline">
                  {cd.username}
                </Link>
              </h1>
              <div className="text-sm text-muted">{cd.party || t("common.independent")}</div>
              <div className="text-sm font-medium mt-1">
                🗳 <span className="capitalize">{cd.office === "president" ? t("office.president") : t("office.congress")}</span> · {dateLabel(cd.cycleStartAt)}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Share
                title={t("candidacy.shareTitle", { name: cd.username, office: cd.office === "president" ? t("office.president") : t("office.congress") })}
                text={t("candidacy.shareText", { name: cd.username })}
                url={candidateShareUrl(cd.username)}
              />
              <ReviewBadge review={cd.review} />
              <ClaimBadge claimStatus={cd.claimStatus} />
              <OriginBadge origin={cd.origin} />
            </div>
          </div>

          <CampaignInfo cd={cd} />

          <Platform cd={cd} token={token} mine={isOwner} onSaved={setCd} />

          <Ama cd={cd} token={token} isOwner={isOwner} onChange={setCd} />

          {!isOwner && <ManageBox candidacyId={cd.id} username={cd.username} onUnlocked={onUnlock} />}
        </>
      )}
    </Page>
  );
}

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* clipboard blocked — user can still select the text manually */
  }
}

/** Shown once after declaring: editing is unlocked by verifying ownership, not by
 * a handed-out token. Points the candidate at the Verify flow below. */
function CreatedBanner() {
  const t = useT();
  return (
    <div className="mt-3 text-sm bg-accent/10 border border-accent/25 rounded-lg p-3">
      <p className="font-semibold">{t("candidacy.created.title")} 🎉</p>
      <p className="text-muted mt-1">{t("candidacy.created.body")}</p>
    </div>
  );
}

/** Claim/manage entry point: self-service ownership proof (rename a WarEra
 * company to a one-time code we verify via the API). Copy/restore the resulting
 * token from the nav account menu. */
function ManageBox({
  candidacyId,
  username,
  onUnlocked,
}: {
  candidacyId: string;
  username: string;
  onUnlocked: (token: string) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-8 border-t border-border pt-4">
      {open ? (
        <Card>
          <VerifyOwnership candidacyId={candidacyId} username={username} onUnlocked={onUnlocked} />
          <p className="text-xs text-muted mt-3">{t("candidacy.restoreHint")}</p>
          <button onClick={() => setOpen(false)} className="text-muted text-sm mt-3">{t("common.cancel")}</button>
        </Card>
      ) : (
        <button onClick={() => setOpen(true)} className="text-muted text-sm hover:text-accent">
          {t("candidacy.areYou", { name: username })} →
        </button>
      )}
    </div>
  );
}

/** Self-service proof: get a nonce, set it as a company name in WarEra, confirm. */
function VerifyOwnership({
  candidacyId,
  username,
  onUnlocked,
}: {
  candidacyId: string;
  username: string;
  onUnlocked: (token: string) => void;
}) {
  const t = useT();
  const [nonce, setNonce] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function start() {
    setBusy(true);
    setErr(null);
    try {
      const { nonce } = await api.startCompanyVerify(candidacyId);
      setNonce(nonce);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    setBusy(true);
    setErr(null);
    try {
      const { editToken: token } = await api.confirmCompanyVerify(candidacyId);
      onUnlocked(token); // parent stores it (reactive) — unlocks every campaign you own
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p className="text-sm font-semibold">{t("candidacy.verifyOwn", { name: username })}</p>
      {!nonce ? (
        <>
          <p className="text-sm text-muted mt-1">{t("candidacy.verifyBlurb")}</p>
          <button disabled={busy} onClick={start} className="bg-accent text-white rounded-lg px-4 py-2 text-sm font-semibold mt-3 disabled:opacity-50">
            {busy ? t("candidacy.starting") : t("candidacy.startVerification")}
          </button>
        </>
      ) : (
        <>
          <ol className="text-sm text-muted mt-1 list-decimal list-inside space-y-1">
            <li>{t("candidacy.renameStep")}</li>
          </ol>
          <div className="flex items-center gap-2 mt-2">
            <code className="flex-1 bg-panel2 border border-border rounded px-2 py-1.5 text-xs break-all">{nonce}</code>
            <button onClick={() => copy(nonce)} className="border border-border rounded px-3 py-1.5 text-sm shrink-0 hover:border-accent">{t("common.copy")}</button>
          </div>
          <p className="text-sm text-muted mt-2">{t("candidacy.verifyStep")}</p>
          <button disabled={busy} onClick={confirm} className="bg-accent text-white rounded-lg px-4 py-2 text-sm font-semibold mt-3 disabled:opacity-50">
            {busy ? t("candidacy.checking") : t("candidacy.verifyNow")}
          </button>
        </>
      )}
      {err && <div className="mt-2"><ErrorBox msg={err} /></div>}
    </div>
  );
}

function Platform({
  cd,
  token,
  mine,
  onSaved,
}: {
  cd: CandidacyDetail;
  token: string | null;
  mine: boolean;
  onSaved: (next: CandidacyDetail) => void;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [introDoc, setIntroDoc] = useState(() => docOrLegacy(cd.introDoc, cd.intro));
  const [party, setParty] = useState(cd.party ?? "");
  const [measures, setMeasures] = useState<DraftMeasure[]>(
    cd.measures.map((m) => ({ title: m.title, detailDoc: docOrLegacy(m.detailDoc, m.detail) })),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!token) return;
    setBusy(true);
    setErr(null);
    try {
      const next = await api.updatePlatform(cd.id, token, {
        introDoc,
        party: party.trim() || undefined,
        measures: measures.map((m) => ({ title: m.title, detailDoc: m.detailDoc })),
      });
      onSaved(next);
      setEditing(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <Card>
        <label className="block text-sm mb-1 text-muted">{t("candidacy.party")}</label>
        <input
          className="w-full bg-panel2 border border-border rounded px-2 py-1.5 text-sm mb-3"
          maxLength={60}
          value={party}
          onChange={(e) => setParty(e.target.value)}
          placeholder={t("candidacy.partyPlaceholder")}
        />
        <label className="block text-sm mb-1 text-muted">{t("candidacy.manifesto")}</label>
        <div className="mb-3">
          <RichTextEditor valueDoc={introDoc} onChange={setIntroDoc} ariaLabel={t("candidacy.manifesto")} />
        </div>
        <label className="block text-sm mb-2 text-muted">{t("candidacy.measures")}</label>
        <MeasuresEditor value={measures} onChange={setMeasures} />
        {err && <div className="mt-3"><ErrorBox msg={err} /></div>}
        <div className="flex gap-2 mt-3">
          <button disabled={busy} onClick={save} className="bg-accent text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
            {busy ? t("common.saving") : t("common.save")}
          </button>
          <button onClick={() => setEditing(false)} className="text-muted text-sm px-3">{t("common.cancel")}</button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      {cd.introDoc || cd.intro ? (
        <RichText doc={cd.introDoc} fallbackText={cd.intro} className="text-sm" />
      ) : (
        <p className="text-muted text-sm">{t("candidacy.noManifesto")}</p>
      )}
      {cd.measures.length > 0 && (
        <ul className="mt-4 space-y-2">
          {cd.measures.map((m) => (
            <li key={m.id} className="border-l-2 border-accent pl-3">
              <div className="font-medium text-sm">{m.title}</div>
              {(m.detailDoc || m.detail) && <RichText doc={m.detailDoc} fallbackText={m.detail} className="text-sm text-muted" />}
            </li>
          ))}
        </ul>
      )}
      {cd.sources.length > 0 && (
        <div className="mt-4 border-t border-border pt-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-1.5">{t("candidacy.sources")}</div>
          <ul className="space-y-1">
            {cd.sources.map((s, i) => (
              <li key={i} className="text-sm">
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-accent underline break-all">
                  {s.label || s.url}
                </a>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted mt-2">{t("candidacy.autoGenerated")}</p>
        </div>
      )}
      {mine && (
        <button onClick={() => setEditing(true)} className="text-accent text-sm mt-4">{t("candidacy.editCampaign")}</button>
      )}
    </Card>
  );
}

/** Owner stats + a link back to the full race/forecast. Stats come from the
 * candidacy detail (read-through cached server-side); hidden gracefully when
 * unavailable. The race date lives in the page header (prominent). */
function CampaignInfo({ cd }: { cd: CandidacyDetail }) {
  const t = useT();
  const s = cd.stats;
  return (
    <Card className="mb-3">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-[11px] uppercase tracking-wide text-muted">{t("candidacy.playerStats")}</div>
        <Link to={`/c/${cd.countryId}/race/${cd.office}`} className="text-accent text-sm shrink-0">
          {t("candidacy.viewRace")} →
        </Link>
      </div>
      {s ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-sm">
          <Stat label={t("stats.level")} value={s.level} />
          <Stat label={t("stats.careerDmg")} value={fmtDmg(s.careerDamage)} sub={s.careerDamageRank ? `#${s.careerDamageRank}` : undefined} />
          <Stat label={t("stats.militaryRank")} value={`#${s.militaryRank}`} />
          <Stat label={t("stats.wealth")} value={fmtDmg(s.wealth)} />
          <Stat label={t("stats.premium")} value={s.premiumMonths ? `${s.premiumMonths}mo` : "—"} />
          <Stat label={t("stats.subscribers")} value={s.subscribers} />
          <Stat label={t("stats.referrals")} value={s.referrals} />
          <Stat label={t("stats.playingSince")} value={sinceLabel(s.createdAt)} />
          <Stat label={t("stats.lastActive")} value={agoLabel(s.lastConnectionAt, t)} />
        </div>
      ) : (
        <p className="text-muted text-sm">{t("candidacy.statsUnavailable")}</p>
      )}
    </Card>
  );
}

function Ama({
  cd,
  token,
  isOwner,
  onChange,
}: {
  cd: CandidacyDetail;
  token: string | null;
  isOwner: boolean;
  onChange: (next: CandidacyDetail) => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState("");
  const [editorKey, setEditorKey] = useState(0); // bump to remount (clear) the editor
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function ask() {
    if (!docHasText(draft)) return;
    setBusy(true);
    setErr(null);
    try {
      const q = await api.askQuestion(cd.id, draft, name.trim() || undefined);
      onChange({ ...cd, questions: [...cd.questions, q] });
      setDraft("");
      setEditorKey((k) => k + 1);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function upvote(qid: string) {
    try {
      const { upvotes } = await api.upvoteQuestion(cd.id, qid);
      onChange({
        ...cd,
        questions: cd.questions
          .map((q) => (q.id === qid ? { ...q, upvotes } : q))
          .sort((a, b) => b.upvotes - a.upvotes || a.createdAt - b.createdAt),
      });
    } catch {
      /* dedup or transient — ignore */
    }
  }

  function applyAnswer(qid: string, answerDoc: string) {
    onChange({
      ...cd,
      questions: cd.questions.map((q) => (q.id === qid ? { ...q, answerDoc, answeredAt: Date.now() } : q)),
    });
  }

  return (
    <div className="mt-6">
      <h2 className="font-semibold mb-3">{t("ama.title")}</h2>

      <Card className="mb-4">
        <RichTextEditor key={editorKey} valueDoc={null} onChange={setDraft} ariaLabel={t("ama.askPlaceholder")} />
        <input
          className="w-full bg-panel2 border border-border rounded px-2 py-1.5 text-sm mt-2"
          maxLength={24}
          placeholder={t("ama.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {err && <div className="mt-2"><ErrorBox msg={err} /></div>}
        <button
          disabled={!docHasText(draft) || busy}
          onClick={ask}
          className="bg-accent text-white rounded-lg px-4 py-2 text-sm font-semibold mt-2 disabled:opacity-50"
        >
          {busy ? t("ama.posting") : t("ama.ask")}
        </button>
      </Card>

      <div className="space-y-3">
        {cd.questions.length === 0 && <p className="text-muted text-sm">{t("ama.noQuestions")}</p>}
        {cd.questions.map((q) => (
          <QuestionRow
            key={q.id}
            candidacyId={cd.id}
            q={q}
            token={isOwner ? token : null}
            onUpvote={() => upvote(q.id)}
            onAnswered={(answer) => applyAnswer(q.id, answer)}
          />
        ))}
      </div>
    </div>
  );
}

function QuestionRow({
  candidacyId,
  q,
  token,
  onUpvote,
  onAnswered,
}: {
  candidacyId: string;
  q: AmaQuestion;
  token: string | null;
  onUpvote: () => void;
  onAnswered: (answerDoc: string) => void;
}) {
  const t = useT();
  const [answering, setAnswering] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!token || !docHasText(draft)) return;
    setBusy(true);
    try {
      await api.answerQuestion(candidacyId, token, q.id, draft);
      onAnswered(draft);
      setAnswering(false);
    } catch {
      /* surfaced minimally; keep editor open */
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex items-start gap-3">
        <button
          onClick={onUpvote}
          className="flex flex-col items-center text-muted hover:text-accent shrink-0"
          title={t("ama.upvote")}
        >
          <span className="leading-none">▲</span>
          <span className="text-xs">{q.upvotes}</span>
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-muted">{t("ama.asked", { name: q.askerName || t("ama.anonymous") })}</div>
          <RichText doc={q.bodyDoc} fallbackText={q.body} className="text-sm" />
          {q.answerDoc || q.answer ? (
            <div className="mt-2 border-l-2 border-accent2 pl-3">
              <div className="text-[11px] text-muted">{t("ama.candidateAnswered")}</div>
              <RichText doc={q.answerDoc} fallbackText={q.answer} className="text-sm" />
            </div>
          ) : token ? (
            answering ? (
              <div className="mt-2">
                <RichTextEditor valueDoc={null} onChange={setDraft} ariaLabel={t("ama.answer")} />
                <div className="flex gap-2 mt-2">
                  <button disabled={!docHasText(draft) || busy} onClick={submit} className="bg-accent text-white rounded px-3 py-1 text-sm disabled:opacity-50">
                    {busy ? t("common.saving") : t("ama.answer")}
                  </button>
                  <button onClick={() => setAnswering(false)} className="text-muted text-sm px-2">{t("common.cancel")}</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAnswering(true)} className="text-accent text-sm mt-2">{t("ama.answer")}</button>
            )
          ) : (
            <div className="text-[11px] text-muted mt-1">{t("ama.unanswered")}</div>
          )}
        </div>
      </div>
    </Card>
  );
}
